import { Checkbox, FormControlLabel, FormGroup } from '@mui/material';
import { cn } from 'cn';
import { useId } from 'react';

import { isFieldRequired, unique } from '@vestigas/utils';

import { ValidationError } from '@vestigas/ui/molecules';

import { useFieldContext } from './form-context';
import { InputLabel } from './InputLabel';

type CheckboxOption = {
  readonly label: string;
  readonly value: string;
};

type P = {
  readonly hint?: string;
  readonly isDisabled?: boolean;
  readonly label?: string;
  readonly options: CheckboxOption[];
} & ComponentStyling;

export const CheckboxGroupField = ({
  className,
  hint,
  isDisabled = false,
  label,
  options,
  style,
}: P) => {
  const field = useFieldContext<string[]>();

  const isRequired = isFieldRequired(
    field.options.form.options.validators,
    field.name,
  );

  const hasErrors = Boolean(field.state.meta.errors.length);

  const id = useId();
  // A checkbox group has no single input a label's `htmlFor` could point at,
  // so the label, the hint and the error are tied to the group via ARIA
  // instead. The hint is a tooltip on the label, which a screen reader never
  // opens, so it is repeated in a visually hidden node the way FieldShell does.
  const labelId = `${id}-label`;
  const errorId = `${id}-error`;
  const hintId = hint ? `${id}-hint` : undefined;

  const describedBy = [hintId, hasErrors ? errorId : undefined]
    .filter((value) => value !== undefined)
    .join(' ');

  const selected = new Set(field.state.value);

  const toggle = (value: string) => {
    const next = new Set(selected);

    if (!next.delete(value)) {
      next.add(value);
    }

    // The value keeps the order of `options`, not the order of clicking, so
    // consumers can rely on it however the selection was arrived at.
    field.handleChange(
      options.flatMap((option) =>
        next.has(option.value) ? [option.value] : [],
      ),
    );
  };

  return (
    <div className={cn('flex flex-col', className)} style={style}>
      {Boolean(label) && (
        <InputLabel hint={hint} id={labelId} isRequired={isRequired}>
          {label}
        </InputLabel>
      )}
      {hintId ? (
        <span className="sr-only" id={hintId}>
          {hint}
        </span>
      ) : null}
      <FormGroup
        aria-describedby={describedBy.length > 0 ? describedBy : undefined}
        aria-labelledby={label ? labelId : undefined}
        role="group"
      >
        {options.map((option) => (
          <FormControlLabel
            key={option.value}
            control={
              <Checkbox
                checked={selected.has(option.value)}
                disabled={isDisabled}
                onChange={() => {
                  toggle(option.value);
                }}
              />
            }
            label={option.label}
            slotProps={{
              typography: {
                className: 'text-sm',
              },
            }}
          />
        ))}
      </FormGroup>
      {hasErrors ? (
        <div id={errorId}>
          <ValidationError>
            {unique(
              // Typed by hand: the sibling fields' untyped version fails the
              // type-aware lint in files without recorded suppressions.
              field.state.meta.errors.map(
                ({ message }: { message: string }) => message,
              ),
            ).join(', ')}
          </ValidationError>
        </div>
      ) : null}
    </div>
  );
};
